import { useEffect, useMemo, useRef } from "react";

import FormStepTypes from "constants/FormStepTypes";

import {
  getFormFlow,
  resetRequestFormAnalyticsState,
  resolveAbandonSectionName,
  setConfirmationStartedAt,
  setFormStartedAt,
  setRequestFormAnalyticsContext,
  trackQuoteConfirmationViewed,
  trackQuoteFormEntered,
  trackRouteLeaveAbandon,
  trackTabCloseAbandon,
} from "./requestFormAnalytics";

type UseRequestFormAnalyticsSessionParams = {
  enabled?: boolean;
  isEditQuoteRequest?: boolean;
  isEditShipment?: boolean;
  activeStep: string | null;
};

export function useRequestFormAnalyticsSession({
  enabled = true,
  isEditQuoteRequest = false,
  isEditShipment = false,
  activeStep,
}: UseRequestFormAnalyticsSessionParams) {
  const formFlow = useMemo(
    () => getFormFlow({ isEditQuoteRequest, isEditShipment }),
    [isEditQuoteRequest, isEditShipment],
  );
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  useEffect(() => {
    if (!enabled) return;

    // Module-level session flags must not leak across SPA navigations.
    resetRequestFormAnalyticsState();
    setRequestFormAnalyticsContext({ formFlow });
    setFormStartedAt(Date.now());
    trackQuoteFormEntered();
  }, [enabled, formFlow]);

  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      trackTabCloseAbandon(resolveAbandonSectionName(activeStepRef.current));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      trackRouteLeaveAbandon(resolveAbandonSectionName(activeStepRef.current));
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (activeStep !== FormStepTypes.CONFIRMATION) return;

    setConfirmationStartedAt(Date.now());
    trackQuoteConfirmationViewed();
  }, [enabled, activeStep]);
}
